import { getLayout, getListOfLayouts } from "../transport/layouts";
import curry from "../lib/core/functional/curry";

import { makeSticky } from "./stickyBTests";

const PARSER = new DOMParser();

const LOCALSTORAGE_KEY = "rm-lddebugger-ablists";

function deleteFromLocal() {
    window.localStorage.removeItem(LOCALSTORAGE_KEY);
}

function storeInLocal(payload) {
    window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(payload));
    return payload;
}

function getFromLocal() {
    const item = window.localStorage.getItem(LOCALSTORAGE_KEY);
    if (item) {
        return JSON.parse(item);
    } else {
        return null;
    }
}

function populateOptions(select, abTests, currentQueryParams) {
    let selected = false;

    function makeOption(optionGroup, abTest) {
        const [queryName, queryValue, name, lastUpdate, authorName] = abTest;
        const option = document.createElement("option");
        option.value = `${queryName}:${queryValue}`;
        option.textContent = `${name} - ${authorName} - ${lastUpdate}`;
        if (currentQueryParams.get(queryName) === queryValue && !selected) {
            selected = true;
            option.selected = true;
        }
        optionGroup.appendChild(option);
        return optionGroup;
    }

    for (const [layoutName, abTestsInfo] of abTests) {
        const optionGroup = document.createElement("optgroup");
        optionGroup.label = layoutName;
        abTestsInfo.reduce(makeOption, optionGroup);
        select.appendChild(optionGroup);
    }
}

const getAbTestInfo = curry(function getAbTestInfo(authors, abTestElement) {
    const queryParam = abTestElement.getAttribute("query-param");
    const queryValue = abTestElement.getAttribute("query-value");
    const authorId = abTestElement.getAttribute("data-rm-created-by");
    const name = abTestElement.getAttribute("name");
    const lastUpdate = abTestElement.getAttribute("data-rm-created-on");
    const authorName = parseInt(authorId, 10) in authors ? authors[authorId].title : "Anonymous";

    return [queryParam, queryValue, name, lastUpdate, authorName];
});

function getAbTestsList(authors, doc) {
    return Array.from(doc.querySelectorAll("abtest")).map(getAbTestInfo(authors));
}

const parseLayoutResponse = curry(function parseLayoutResponse(authors, layoutResponse) {
    const { title, content } = layoutResponse;
    const doc = PARSER.parseFromString(content, "application/xml");
    return [title, getAbTestsList(authors, doc)];
});

const getLayoutInfo = curry(function getLayoutInfo(authors, layout) {
    return getLayout(layout.name).then(parseLayoutResponse(authors));
});

function entryFromAuthor(author) {
    return [author.id, author];
}

function doesLayoutHaveBtests(layoutInfo) {
    const [_name, abTests] = layoutInfo;
    return abTests.length > 0;
}

function filterOutLayoutsWithoutBtests(allLayouts) {
    return allLayouts.filter(doesLayoutHaveBtests);
}

function getIndividualLayouts(layoutEditorResponse) {
    const { layouts, authors } = layoutEditorResponse;
    const indexedAuthors = Object.fromEntries(Object.values(authors).map(entryFromAuthor));
    return Promise.all(layouts.map(getLayoutInfo(indexedAuthors)))
        .then(filterOutLayoutsWithoutBtests)
        .then(storeInLocal);
}

function getListOfCompiledAbTests() {
    const local = getFromLocal();
    if (local) {
        return Promise.resolve(local);
    } else {
        return getListOfLayouts().then(getIndividualLayouts);
    }
}

function getAbTestQueryParams(abTestInfo) {
    const [queryName, queryValue] = abTestInfo;
    return [queryName, queryValue];
}

function getSimplifiedAbtest(abTestsByLayout) {
    const [_layoutName, abTests] = abTestsByLayout;
    return abTests.map(getAbTestQueryParams);
}

function renderUI(abTestsByLayout) {
    const currentQueryParams = new URLSearchParams(document.location.search);

    const holder = document.createElement("div");
    holder.classList.add("rm-lddebugger-btests-dropdown-holder");

    const dropdown = document.createElement("select");
    dropdown.classList.add("rm-lddebugger-btests-dropdown");

    const simplifiedAbTestList = abTestsByLayout.flatMap(getSimplifiedAbtest);

    function pinSelectedBtest() {
        const abtest = dropdown.value.split(":");
        makeSticky([abtest]);
    }

    function switchToSelectedBtest() {
        const [newAbTestName, newAbTestValue] = dropdown.value.split(":");
        const newUrl = new URL(document.location.href);
        const currentSearch = new URLSearchParams(newUrl.search);
        for (const [abTestName, abTestValue] of simplifiedAbTestList) {
            if (currentSearch.has(abTestName) && currentSearch.get(abTestName) === abTestValue) {
                currentSearch.delete(abTestName);
            }
        }
        currentSearch.set(newAbTestName, newAbTestValue);
        newUrl.search = `?${currentSearch.toString()}`;
        document.location.href = newUrl.href;
    }

    const pinButton = document.createElement("button");
    pinButton.classList.add(
        "rm-lddebugger-button",
        "rm-lddebugger-btests-dropdown-button",
        "rm-lddebugger-btests-dropdown-button-pin"
    );
    pinButton.textContent = "pin";
    pinButton.addEventListener("click", pinSelectedBtest, { passive: true });

    const switchButton = document.createElement("button");
    switchButton.classList.add(
        "rm-lddebugger-button",
        "rm-lddebugger-btests-dropdown-button",
        "rm-lddebugger-btests-dropdown-button-go-to"
    );
    switchButton.textContent = "go to";
    switchButton.addEventListener("click", switchToSelectedBtest, { passive: true });

    function refreshUI() {
        holder.remove();
        showBTests("clear");
    }

    const refetchButton = document.createElement("button");
    refetchButton.classList.add(
        "rm-lddebugger-button",
        "rm-lddebugger-btests-dropdown-button",
        "rm-lddebugger-btests-dropdown-button-fetch"
    );
    refetchButton.textContent = "fetch";
    refetchButton.title = "Reloads UI and refetches data from back end, use with caution";
    refetchButton.addEventListener("click", refreshUI, { passive: true });

    populateOptions(dropdown, abTestsByLayout, currentQueryParams);

    holder.appendChild(dropdown);
    holder.appendChild(switchButton);
    holder.appendChild(pinButton);
    holder.appendChild(refetchButton);
    document.body.appendChild(holder);
}

export default function showBTests(options) {
    if (options === "clear") {
        deleteFromLocal();
    }
    getListOfCompiledAbTests().then(renderUI);
}
